/* eslint-disable react-hooks/rules-of-hooks */
/** @jsx jsx */
// External libraries
import { useEffect } from 'react';
import { jsx } from '@emotion/core';
// @ts-expect-error TODO: checkout/react-components
import { PageMessage } from '@checkout/react-components';
import {
  CI,
  getContent,
  getGlobals,
  getUnifiedLoginContextData,
  useEndAndStartCi,
} from '@/lib';
import { useStoreState } from '@/store';

// Internal modules
import { Form } from '../Form';
import { Layout } from '../Layout';
import { hasTreatment } from '@/lib/features/utils';
import { pageWarningContainerStyles } from './styles';
import {
  ApplePayButtonWrapper,
  BaslHaveAnAccountLogIn,
  HaveAnAccountLogIn,
  LazyInventorySoldOutInterstitial,
  LazyVenmoMessage,
} from './Components';
import { VENMO_OVERLAY_VARIANTS } from '../VenmoMessage/constants';
import { PageLevelError } from '../PageLevelError';
import { features, useCheckInventorySoldout, useMountLogging } from '@/lib';
import { getParams } from '@/lib/utils/params';
import { RealMoneyGamingPage } from '@/pages';
import { MainContentAreaComponent } from './StyledComponents';
import { useDeferredFeatures } from '../../lib/features/deferredFeatures';
import {
  checkFrameBust,
  handleBlockedDomesticTransactions,
  handleSignupAndGuestIneligible,
  logIncontext,
} from './utils';
import { conditionallyCalculateShippingAndTaxes } from '@/components/Shipping/utils';
import {
  EVENTS,
  startLogPerformanceTime,
  triggerInitialCPLEvent,
} from '@/lib/logger/cplUtils';
import { getFptiFlowName } from '@/lib/features/utils';
// @ts-expect-error
import { fetchInitialData } from '@/components/App/api';
import { useApplePay } from '@/lib/reactHooks/useApplePay';
import { logBillingPageShown } from './fpti';
import { logger } from '@/lib/logger';
import { runHealthCheck } from '@/lib/health-check';
import { usePasskeyAvailable } from '@/lib/reactHooks/usePasskeyAvailable';
import { LazyPasskeyEnrollmentPage } from '@/pages/PasskeyEnrollmentPage/Components';

export const Billing = (): React.ReactElement => {
  const isRMGMerchant = hasTreatment('FEATURE_RMG_LANDING_PAGE');
  const isINForcedGuest = features.isActive('shouldDisableSignupForIndia');

  useMountLogging('Billing');
  useDeferredFeatures();
  useCheckInventorySoldout();
  // Trigger passkey availability check. There is no action yet based
  // on the result. This check is only browser and user-agent check.
  // NOTE: we are not using the response here, as we dont want any re-renders
  // due to passkey availability checks
  usePasskeyAvailable();

  useEffect(() => {
    try {
      // We dont need setTimeout here as the UI wont wait for
      // APIs at this point, and most modules is rendered already
      // In-case we introduce more lazyLoading below this component
      // then there might be a case where we need setTimeout here too
      runHealthCheck({
        event: 'reactMounted',
        eventSrc: 'billing.tsx',
      });
    } catch (err) {
      const { message, name, stack } =
        err instanceof Error ? err : ({} as Partial<Error>);
      logger.error('ERROR_LOGGING_HEALTHCHECK_BILLING', {
        message,
        name,
        stack,
      });
    }

    // We should generally refrain from adding more checks in here as it's not
    // scalable and bug prone to introduce complex logic at the start of the App
    // NOTE: anything that goes here might be required in eventHandlers.js, in
    // handleCountryChange function - user country change using country dropdown
    // TODO: setup a country change handler to handle hooks and
    // functions to be called when user change country using country dropdown
    handleBlockedDomesticTransactions();
    handleSignupAndGuestIneligible();
    checkFrameBust();
    logIncontext();
    startLogPerformanceTime(EVENTS.USER_INTERACTION);
    logBillingPageShown();

    fetchInitialData().then(() => {
      const fptiFlowName = getFptiFlowName({
        clientFeatures: features.config,
      });

      conditionallyCalculateShippingAndTaxes();
      triggerInitialCPLEvent(fptiFlowName, EVENTS.REVIEW_PAGE_RENDER);
      startLogPerformanceTime(EVENTS.USER_INTERACTION);
    });
  }, []);

  const showPasskeyEnrollmentPage = useStoreState(
    ({ ui }) => ui.showPasskeyEnrollmentPage,
  );

  if (isRMGMerchant) {
    return <RealMoneyGamingPage />;
  }

  const { isBasl } = getGlobals();
  const { showApplePayButton } = useApplePay({
    page: 'billing',
  });
  // We don't want to display Apple Pay button by default,
  // so assume it has been seen unless told otherwise.
  const { applePayButtonSeen = true } = getUnifiedLoginContextData() || {};
  // applePayButton by default is null.
  const strictApplePayButtonNotSeen =
    !applePayButtonSeen && (applePayButtonSeen !== null || isBasl);

  const isInventorySoldOutOpen = useStoreState(
    ({ ui }) => ui.isInventorySoldOutOpen,
  );

  const showVenmoMessage = useStoreState(({ ui }) => ui.showVenmoMessage);
  const showVenmoInterstitial =
    showVenmoMessage &&
    !features.hasFeature('venmoOverlay', VENMO_OVERLAY_VARIANTS.HEADER);
  const pageLevelError = useStoreState(({ ui }) => ui.pageLevelError);
  const warningMessage = useStoreState(({ ui }) => ui.pageWarningMessage);

  const styleObject = { container: pageWarningContainerStyles };
  const buttonSessionId = getParams().buttonSessionId;

  // Subscribing to this value will ensure the form rerenders on language change
  useStoreState(({ ui }) => ui.currentLanguage);
  useStoreState(({ ui }) => ui.country);

  useEndAndStartCi({
    end: CI.Event.APP_ROUTING,
    endStatus: CI.Status.Ok,
    start: CI.Event.GUEST_XO_SIGNUP_FORM_USER_INPUT,
  });

  return showPasskeyEnrollmentPage ? (
    <LazyPasskeyEnrollmentPage />
  ) : (
    <Layout pageName="billing" showValueProps={true}>
      <MainContentAreaComponent>
        {showApplePayButton && strictApplePayButtonNotSeen && (
          <ApplePayButtonWrapper showDividerOrText={!isBasl} />
        )}
        {isBasl && (
          <BaslHaveAnAccountLogIn isBaslApplePay={showApplePayButton} />
        )}

        {Boolean(pageLevelError.length) && <PageLevelError />}
        {Boolean(warningMessage.length) && (
          <PageMessage
            message={getContent(warningMessage)}
            styleOverrides={styleObject}
            type="warn"
          />
        )}

        {isInventorySoldOutOpen && <LazyInventorySoldOutInterstitial />}
        {showVenmoInterstitial && (
          <LazyVenmoMessage buttonSessionId={buttonSessionId} />
        )}
        <Form />

        {!isBasl && !isINForcedGuest && <HaveAnAccountLogIn />}
      </MainContentAreaComponent>
    </Layout>
  );
};
